<template>
  <v-container fluid>
    <v-card class="mb-5">
        <v-toolbar flat color="white">
          <v-toolbar-title class="green--text">Layout</v-toolbar-title>
          <v-divider
            class="mx-2"
            inset
            vertical
          ></v-divider>
          <v-spacer></v-spacer>
          <v-spacer></v-spacer>
          <v-spacer></v-spacer>
          <v-spacer></v-spacer>
          <v-spacer></v-spacer>
          <v-spacer></v-spacer>
          <v-menu
            bottom
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="success"
                dark
                v-bind="attrs"
                v-on="on"
              >
                Space Actions
              </v-btn>
            </template>

            <v-list>
              <v-list-item link @click="saveLayoutData">
                <v-list-item-icon>
                  <v-icon color="primary">add_box</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Save Layout</v-list-item-title>
                </v-list-item-content>
              </v-list-item>     
              <v-list-item link @click="confirmLoadLayoutFromS3">
                <v-list-item-icon>
                  <v-icon color="primary">cloud_download</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Load Layout from S3</v-list-item-title>
                </v-list-item-content>
              </v-list-item>     
            </v-list>
          </v-menu>
          <v-divider
            class="mx-2"
            inset
            vertical
          ></v-divider>

      </v-toolbar>
      
      <v-card-text>
        <div class="position-relative">
          <!-- Left-side spaces menu -->
          <div 
            class="spaces-side-menu"
            :class="{ 'spaces-side-menu-expanded': showSpacesMenu && layoutImageUrl }"
            @mouseenter="layoutImageUrl && (showSpacesMenu = true)"
            @mouseleave="showSpacesMenu = false"
            v-if="layoutImageUrl"
          >
            <div class="spaces-side-menu-tab">
              <v-icon>mdi-floor-plan</v-icon>
            </div>
            <div class="spaces-side-menu-content">
              <h3 class="pa-3 mb-0">Spaces</h3>
              <v-divider></v-divider>
              
              <!-- Collapsible search bar -->
              <v-expand-transition>
                <div v-if="showSpacesSearchBar">
                  <v-text-field
                    v-model="spacesSearch"
                    append-icon="mdi-magnify"
                    label="Search Spaces"
                    dense
                    hide-details
                    class="mx-3 mt-3 mb-2"
                    @keydown.esc="showSpacesSearchBar = false"
                  ></v-text-field>
                </div>
              </v-expand-transition>
              
              <div class="d-flex justify-end px-3 py-1">
                <v-btn 
                  x-small 
                  icon 
                  @click="showSpacesSearchBar = !showSpacesSearchBar"
                  :color="showSpacesSearchBar ? 'primary' : ''"
                >
                  <v-icon small>mdi-magnify</v-icon>
                </v-btn>
              </div>
              
              <v-list dense class="spaces-list">
                <div v-if="filteredSpaces.length === 0" class="pa-3 text-center grey--text">
                  No spaces found. Draw spaces on the layout.
                </div>
                <v-list-item-group v-else>
                  <v-list-item
                    v-for="space in filteredSpaces"
                    :key="space.uuid"
                    @mouseover="hoveredSpace = space" 
                    @mouseleave="hoveredSpace = null"
                    class="space-list-item"
                  >
                    <v-list-item-icon>
                      <v-icon color="green">mdi-floor-plan</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="space-name">
                        <v-edit-dialog
                          :return-value.sync="space.assetName"
                          @save="updateSpaceName(space)"
                          large
                          persistent
                        >
                          <span class="space-name-display">{{ space.assetName }}</span>
                          <template v-slot:input>
                            <v-text-field
                              v-model="space.assetName"
                              label="Edit Space Name"
                              single-line
                              counter
                              autofocus
                              class="space-name-edit"
                            ></v-text-field>
                          </template>
                        </v-edit-dialog>
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        <div class="mt-1">
                          <template v-if="getAssetInSpace(space).length > 0">
                            <v-chip
                              v-for="asset in getAssetInSpace(space)"
                              :key="asset.uuid"
                              x-small
                              class="mr-1 mb-1"
                              :color="getAssetTypeColor(asset.category)"
                              text-color="white"
                            >
                              <v-icon left x-small>{{ getAssetIcon(asset) }}</v-icon>
                              {{ getAssetDisplayName(asset) }}
                            </v-chip>
                          </template>
                          <span v-else class="grey--text text--lighten-1">
                            <v-icon x-small class="mr-1">mdi-tools</v-icon>
                            No assets
                          </span>
                        </div>
                        <div class="mt-2">
                          <v-switch
                            v-model="space.allowOverlap"
                            @change="updateSpaceAllowOverlap(space)"
                            label="Allow Overlap"
                            color="primary"
                            dense
                            hide-details
                            class="ma-0"
                          ></v-switch>
                        </div>
                        <div class="mt-1 grey--text text--darken-1" v-if="space.createdAt">
                          <v-icon x-small class="mr-1">mdi-calendar</v-icon>
                          {{ space.createdAt }}
                        </div>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn
                        x-small
                        icon
                        color="error"
                        @click.stop="confirmDeleteSpace(space)"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list-item-group>
              </v-list>
            </div>
          </div>
          
          <!-- Right-side asset menu -->
          <div 
            class="asset-side-menu"
            :class="{ 'asset-side-menu-expanded': showSideMenu && layoutImageUrl }"
            @mouseenter="layoutImageUrl && (showSideMenu = true)"
            @mouseleave="showSideMenu = false"
            v-if="layoutImageUrl"
          >
            <div class="asset-side-menu-tab">
              <v-icon>mdi-tools</v-icon>
            </div>
            <div class="asset-side-menu-content">
              <h3 class="pa-3 mb-0">Assets</h3>
              <v-divider></v-divider>
              
              <!-- Collapsible search bar -->
              <v-expand-transition>
                <div v-if="showSearchBar">
            <v-text-field
                    v-model="sideMenuSearch"
              append-icon="mdi-magnify"
              label="Search Assets"
              dense
                    hide-details
                    class="mx-3 mt-3 mb-2"
                    @keydown.esc="showSearchBar = false"
            ></v-text-field>
            </div>
              </v-expand-transition>
              
              <div class="d-flex justify-end px-3 py-1">
                <v-btn 
                  x-small 
                  icon 
                  @click="showSearchBar = !showSearchBar"
                  :color="showSearchBar ? 'primary' : ''"
                >
                  <v-icon small>mdi-magnify</v-icon>
                </v-btn>
            </div>
            
              <v-list dense class="asset-list">
                <v-subheader>Available Assets</v-subheader>
                <v-list-item-group>
                  <v-list-item
                    v-for="item in filteredAvailableAssets"
                    :key="item.uuid"
                @mouseover="hoveredAsset = item" 
                @mouseleave="hoveredAsset = null"
                    draggable="true"
                    @dragstart="handleDragStart($event, item)"
                    :class="{ 'asset-item-selected': item.uuid === currentIcon?.uuid }"
                    @click="selectAsset(item)"
              >
                    <v-list-item-icon>
                      <v-icon :color="getAssetColor(item)">
                      {{ getAssetIcon(item) }}
                    </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ getAssetDisplayName(item) }}</v-list-item-title>
                      <v-list-item-subtitle>
                        <v-chip x-small :color="getAssetTypeColor(item.category)" text-color="white">
                          {{ item.category }}
                  </v-chip>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list-item-group>
                
                <v-divider class="my-2"></v-divider>
                <v-subheader>Placed Assets</v-subheader>
                
                <v-list-item-group>
                  <v-list-item
                    v-for="item in filteredPlacedAssets"
                    :key="item.uuid"
                @mouseover="hoveredAsset = item" 
                @mouseleave="hoveredAsset = null"
                    @click="selectPlacedAsset(item)"
                    class="placed-asset-item"
              >
                    <v-list-item-icon>
                      <v-icon :color="getAssetColor(item)">
                      {{ getAssetIcon(item) }}
                    </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ getAssetDisplayName(item) }}</v-list-item-title>
                      <v-list-item-subtitle>
                        <v-chip x-small :color="getAssetTypeColor(item.category)" text-color="white" class="mr-1">
                          {{ item.category }}
                  </v-chip>
                        <template v-if="getSpacesForAsset(item).length > 0">
                      <v-chip
                            x-small
                        color="primary"
                        text-color="white"
                      >
                            {{ getSpacesForAsset(item).length }} space(s)
                      </v-chip>
                    </template>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                  <v-btn
                        x-small
                        icon
                    color="error"
                        @click.stop="removeAsset(item)"
                  >
                        <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list-item-group>
              </v-list>
            </div>
          </div>
          
          <v-row>
            <v-col cols="12">
              <h3 class="mb-3">Draw Area</h3>
              <div 
                class="canvas-container" 
                :style="canvasContainerStyle"
                @dragover="handleDragOver($event)"
                @drop="handleDrop($event)"
              >
                <space-editor 
                  ref="spaceEditor"
                  :layout.sync="layoutImageUrl" 
                  :width="canvasDimensions.width" 
                  :height="canvasDimensions.height"
                  :placed-assets="placedAssets"
                  :hovered-space="hoveredSpace"
                  :hovered-asset="hoveredAsset"
                  :get-asset-icon="getAssetIcon"
                  :get-asset-color="getAssetColor"
                  :spaces="spaces"
                  @space-created="handleSpaceCreated"
                  @spaces-updated="handleSpacesUpdated"
                  @layout-selected="handleLayoutSelected"
                  @icon-in-space="handleIconInSpace"
                  @icon-in-spaces="handleIconInSpaces"
                  @asset-drag-start="handleAssetDragStart"
                  @asset-position-updated="handleAssetPositionUpdated"
                  @asset-drag-end="handleAssetDragEnd"
                  @asset-in-new-space="handleAssetInNewSpace"
                  :lineWidth="2"
                />
              </div>
            </v-col>
          </v-row>
        </div>
        
        <!-- Delete Confirmation Dialog -->
        <v-dialog v-model="deleteDialog" max-width="400px">
          <v-card>
            <v-card-title class="headline">Delete Space</v-card-title>
            <v-card-text>
              Are you sure you want to delete the space "{{ spaceToDelete ? spaceToDelete.name : '' }}"?
              This action cannot be undone.
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="blue darken-1" text @click="deleteDialog = false">Cancel</v-btn>
              <v-btn color="error" text @click="deleteSpace">Delete</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        
        <!-- Load Layout Confirmation Dialog -->
        <v-dialog v-model="loadLayoutDialog" max-width="400px">
          <v-card>
            <v-card-title class="headline">Load Layout from S3</v-card-title>
            <v-card-text>
              Loading a layout from S3 will discard all unsaved changes, including equipment placements and space modifications.
              <br><br>
              Are you sure you want to continue?
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="blue darken-1" text @click="loadLayoutDialog = false">Cancel</v-btn>
              <v-btn color="primary" text @click="proceedWithLoadLayout">Continue</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import store from '@/store'
import { mapState, mapActions } from 'vuex'  
import SpaceEditor from '@/components/SpaceEditor.vue';
import ShortUniqueId from 'short-unique-id';
import { CallDialog } from '@/utils'
import { Storage } from 'aws-amplify';

export default {
  name: 'Layout',
  components: {
    SpaceEditor
  },
  data() {
    return {
      layoutId: 0,
      backgroundFile: null,
      layoutImageUrl: null,
      isImageLoading: false,
      search: '',
      spacesSearch: '',
      equipmentSearch: '',
      placedEquipmentSearch: '',
      sideMenuSearch: '',
      showSideMenu: false,
      showSpacesMenu: false,
      showSearchBar: false,
      showSpacesSearchBar: false,
      headers: [
        { text: 'Name', value: 'name' },
        { text: 'Layout', value: 'layout' },
        { text: 'Equipment', value: 'cameras' },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
      currentIcon: null,
      iconInSpace: null,
      iconInSpaces: [],
      deleteDialog: false,
      loadLayoutDialog: false,
      spaceToDelete: null,
      canvasDimensions: { width: 600, height: 400 },
      canvasContainerStyle: { position: 'relative', width: '100%', border: '1px solid #ccc', backgroundColor: '#f8f8f8' },
      resizeTimeout: null,
      hoveredSpace: null,
      hoveredAsset: null,
      draggedAsset: null
    };
  },
  computed: {
    availableAssets() {
      // Return only assets that haven't been placed yet
      if (!this.assets) return [];
      
      return this.assets.filter(item => !item.placed);
    },
    filteredAvailableAssets() {
      // Filter available assets based on side menu search
      if (!this.availableAssets) return [];
      if (!this.sideMenuSearch) return this.availableAssets;
      
      const search = this.sideMenuSearch.toLowerCase();
      return this.availableAssets.filter(item => 
        item.assetName.toLowerCase().includes(search) || 
        item.category.toLowerCase().includes(search)
      );
    },
    filteredPlacedAssets() {
      // Filter placed assets based on side menu search
      if (!this.placedAssets) return [];
      if (!this.sideMenuSearch) return this.placedAssets;
      
      const search = this.sideMenuSearch.toLowerCase();
      return this.placedAssets.filter(item => 
        item.assetName.toLowerCase().includes(search) || 
        item.category.toLowerCase().includes(search) ||
        this.getSpacesForAsset(item).some(space => space.assetName.toLowerCase().includes(search))
      );
    },
    filteredSpaces() {
      // Filter spaces based on spaces search input
      if (!this.spaces) return [];
      if (!this.spacesSearch) return this.spaces;
      
      const search = this.spacesSearch.toLowerCase();
      return this.spaces.filter(space => 
        space.assetName.toLowerCase().includes(search) || 
        this.getAssetInSpace(space).some(asset => 
          asset.assetName.toLowerCase().includes(search) || 
          asset.category.toLowerCase().includes(search)
        )
      );
    },
    allAssets() {
      // Return all assets, both available and placed
      if (!this.assets) return [];
      
      // Ensure placedAssets is an array
      const placedAssets = this.placedAssets || [];
      
      return this.assets.map(item => {
        // For placed assets, add position and spaces information
        if (item.placed) {
          const placedItem = placedAssets.find(p => p.uuid === item.uuid);
          if (placedItem) {
            return {
              ...item,
              position: placedItem.position,
              spaces: placedItem.spaces
            };
          }
        }
        return item;
      });
    },
    spaces: {
      get() {
        return this.$store.state.host.spaces;
      },
      set(value) {
        this.$store.commit('host/setSpaces', value);
      }
    },
    placedAssets: {
      get() {
        return this.$store.state.host.placedAssets;
      },
      set(value) {
        this.$store.commit('host/setPlacedAssets', value);
      }
    },
    assets: {
      get() {
        return this.$store.state.host.assets;
      },
      set(value) {
        this.$store.commit('host/setAssets', value);
      }
    },
    removedSpaces: {
      get() {
        return this.$store.state.host.removedSpaces;
      },
      set(value) {
        this.$store.commit('host/setRemovedSpaces', value);
      }
    },
    ...mapState({
      hostId: state => state.session.hostId,
      property: state => state.host.property,
    }),
  },
  methods: {
    updateContainerStyle() {
      // Calculate the scaling factor to fit within the column
      // Use nextTick to ensure the DOM is updated
      this.$nextTick(() => {
        const containerWidth = document.querySelector('.canvas-container')?.parentElement?.clientWidth || 600;
        const scale = Math.min(1, containerWidth / this.canvasDimensions.width);
        
        // Calculate height for canvas container based on whether a layout image is selected
        const canvasHeight = this.layoutImageUrl 
          ? Math.min(1000, this.canvasDimensions.height) * scale 
          : Math.min(200, this.canvasDimensions.height * scale);
        
        this.canvasContainerStyle = {
          position: 'relative',
          height: 'auto', // Allow container to adjust to content height
          minHeight: `${canvasHeight}px`, // Set minimum height based on canvas
          width: '100%',
          border: '1px solid #ccc',
          backgroundColor: '#f8f8f8'
        };
        
        console.log('Container styles updated with scale factor:', scale);
      });
    },
    handleLayoutSelected(data) {
      console.log('===== HANDLE LAYOUT SELECTED STARTED =====');
      console.log('Received layout data:', {
        hasFile: data.file ? true : false,
        fileName: data.file ? data.file.name : 'none',
        hasImageUrl: data.imageUrl ? true : false,
        dimensions: data.dimensions ? `${data.dimensions.width}x${data.dimensions.height}` : 'none',
        currentLayoutImageUrl: this.layoutImageUrl ? 'exists' : 'null'
      });
      
      // Set loading state
      this.isImageLoading = true;
      
      // Store the file and URL
      this.backgroundFile = data.file;
      this.layoutImageUrl = data.imageUrl;
      
      console.log('Updated layout state:', {
        backgroundFile: this.backgroundFile ? this.backgroundFile.name : 'null',
        layoutImageUrl: this.layoutImageUrl ? 'set' : 'null'
      });
      
      // Update canvas dimensions, capping height at 1000px if needed
      if (data.dimensions) {
        const originalWidth = data.dimensions.width;
        const originalHeight = data.dimensions.height;
        
        // If height exceeds 1000px, maintain aspect ratio
        if (originalHeight > 1000) {
          const aspectRatio = originalWidth / originalHeight;
          const newHeight = 1000;
          const newWidth = Math.round(newHeight * aspectRatio);
          
          this.canvasDimensions = { 
            width: newWidth, 
            height: newHeight 
          };
          
          console.log(`Image resized from ${originalWidth}x${originalHeight} to ${newWidth}x${newHeight} to fit height limit`);
        } else {
          this.canvasDimensions = { 
            width: originalWidth, 
            height: originalHeight 
          };
        }
      }
      
      // Update container style
      this.updateContainerStyle();
      
      // Update the SpaceEditor component
      this.$nextTick(() => {
        if (this.$refs.spaceEditor) {
          console.log('Updating SpaceEditor with dimensions:', this.canvasDimensions);
          this.$refs.spaceEditor.updateCanvasDimensions();
        }
        
        this.isImageLoading = false;
        
        // Show appropriate message based on whether layout was added or cleared
        if (this.layoutImageUrl) {
          this.$emit('show-snackbar', 'Layout image uploaded successfully');
          console.log('Layout image set with dimensions:', this.canvasDimensions);
          
          // Clear spaces when a new layout is selected
          console.log('Clearing spaces and equipment for new layout');
          console.log('Current state before clearing:', {
            spacesCount: this.spaces ? this.spaces.length : 0,
            hasIconInSpace: this.iconInSpace ? true : false,
            iconInSpacesCount: this.iconInSpaces ? this.iconInSpaces.length : 0,
            placedEquipmentsCount: this.placedEquipments ? this.placedEquipments.length : 0
          });
          
          this.spaces = [];
          this.iconInSpace = null;
          this.iconInSpaces = [];
          this.placedEquipments = [];
          
          // Reset equipment data
          if (this.equipments && this.equipments.length > 0) {
            console.log(`Resetting ${this.equipments.length} equipment items`);
            this.equipments.forEach(item => {
              item.inSpaces = [];
              item.placed = false;
              item.position = null;
            });
          }
          
          // Reset current icon
          this.currentIcon = null;
          
          console.log('State after clearing:', {
            spacesCount: this.spaces ? this.spaces.length : 0,
            hasIconInSpace: this.iconInSpace ? true : false,
            iconInSpacesCount: this.iconInSpaces ? this.iconInSpaces.length : 0,
            placedEquipmentsCount: this.placedEquipments ? this.placedEquipments.length : 0,
            hasCurrentIcon: this.currentIcon ? true : false
          });
        } else {
          console.log('Layout cleared, showing clear notification');
          this.$emit('show-snackbar', 'Layout image cleared');
        }
        console.log('===== HANDLE LAYOUT SELECTED COMPLETED =====');
      });
    },
    handleSpaceCreated(space) {
      // Add created timestamp and default allowOverlap value
      const newSpace = {
        ...space,
        lastUpdateOn: null,
        allowOverlap: false
      };
      
      // Add to spaces array using the setter (append to existing spaces, not replace)
      this.spaces = [newSpace, ...this.spaces];
      
      // Show notification
      this.$emit('show-snackbar', `Space "${newSpace.name}" created successfully`);
    },
    handleSpacesUpdated(internalSpaces) {
      // This method is called when the SpaceEditor's internal spaces are updated
      // We should NOT replace all spaces, as this would remove spaces loaded from the store
      
      // If we're clearing spaces (e.g., when layout changes), we'll get an empty array
      // In that case, we don't want to do anything as the spaces from the store should remain
      if (internalSpaces.length === 0) {
        return;
      }
      
      // For any other case, we'll let the handleSpaceCreated method handle adding new spaces
      // This method should not modify the spaces array directly
      console.log('Internal spaces updated:', internalSpaces);
    },
    handleIconInSpace(space) {
      // Update the iconInSpace data property
      this.iconInSpace = space;
      
      // For backward compatibility
      if (!this.iconInSpaces || this.iconInSpaces.length === 0) {
        this.iconInSpaces = space ? [space] : [];
      }
      
      // Update asset's inSpaces property if an asset is selected
      this.updateAssetSpaces();
      
      // Show notification if space changed
      if (space && this.currentIcon) {
        this.$emit('show-snackbar', `${this.getAssetDisplayName(this.currentIcon)} is now in space "${space.assetName}"`);
      }
    },
    handleIconInSpaces(spaces) {
      // Ensure spaces is an array
      const safeSpaces = spaces || [];
      
      // Update the iconInSpaces data property
      this.iconInSpaces = safeSpaces;
      
      // Update iconInSpace for backward compatibility
      this.iconInSpace = safeSpaces.length > 0 ? safeSpaces[0] : null;
      
      // Update asset's inSpaces property if an asset is selected
      this.updateAssetSpaces();
      
      // Show notification if spaces changed
      if (safeSpaces.length > 0 && this.currentIcon) {
        if (safeSpaces.length === 1) {
          this.$emit('show-snackbar', `${this.getAssetDisplayName(this.currentIcon)} is now in space "${safeSpaces[0].name}"`);
        } else {
          const spaceNames = safeSpaces.map(s => s.name).join('", "');
          this.$emit('show-snackbar', `${this.getAssetDisplayName(this.currentIcon)} is now in multiple spaces: "${spaceNames}"`);
        }
      }
    },
    updateAssetSpaces() {
      if (!this.currentIcon) return;
      
      // Ensure iconInSpaces is an array
      const iconSpaces = this.iconInSpaces || [];
      
      // Find the asset in the assets array
      const assetIndex = this.assets.findIndex(c => c.uuid === this.currentIcon.uuid);
      if (assetIndex !== -1) {
        // Update the asset's inSpaces property with no duplicates (using Set)
        const uniqueSpaceUuids = [...new Set(iconSpaces.map(space => space.uuid))];
        this.assets[assetIndex].inSpaces = uniqueSpaceUuids;
      }
    },
    selectAsset(asset) {
      // Set the current icon to the selected asset
      this.currentIcon = asset;
      
      // Reset icon position in SpaceEditor
      if (this.$refs.spaceEditor) {
        this.$refs.spaceEditor.resetIconPosition();
      }
      
      // Show notification
      this.$emit('show-snackbar', `Selected asset: ${this.getAssetDisplayName(asset)}`);
    },
    placeAssetOnLayout() {
      if (!this.currentIcon || !this.$refs.spaceEditor) return;
      
      // Get current position from SpaceEditor
      const position = this.$refs.spaceEditor.getIconPosition();
      
      // Find the asset in the assets array
      const assetIndex = this.assets.findIndex(c => c.uuid === this.currentIcon.uuid);
      
      if (assetIndex !== -1) {
        // Mark the asset as placed by creating a new array
        const updatedAssets = [...this.assets];
        updatedAssets[assetIndex] = {
          ...updatedAssets[assetIndex],
          placed: true,
          position: { ...position },
          layoutId: this.layoutId
        };
        this.assets = updatedAssets;
        
        // Ensure iconInSpaces is an array
        const iconSpaces = this.iconInSpaces || [];
        
        // Get unique space UUIDs to prevent duplicates
        const uniqueSpaceUuids = [...new Set(iconSpaces.map(space => space.uuid))];
        
        // Add to placed assets array
        const placedItem = {
          uuid: this.currentIcon.uuid,
          assetName: this.currentIcon.assetName,
          category: this.currentIcon.category,
          localIp: this.currentIcon.localIp,
          position: { ...position },
          inSpaces: uniqueSpaceUuids,
          spaces: [...iconSpaces],
          status: this.currentIcon.status,
          layoutId: this.layoutId
        };
        
        try {
          // Initialize placedAssets as empty array if undefined
          if (this.placedAssets === undefined) {
            this.placedAssets = [];
          }
          
          this.placedAssets = [...this.placedAssets, placedItem];
        } catch (error) {
          console.error('Error updating placedAssets:', error);
          console.error('Error stack:', error.stack);
        }
        
        // Reset current icon
        this.currentIcon = null;
        
        // Show notification
        this.$emit('show-snackbar', `Asset placed on layout`);
      }
    },
    removeAsset(asset) {
      // Find the asset in the placed assets array
      const placedIndex = this.placedAssets.findIndex(c => c.uuid === asset.uuid);
      if (placedIndex !== -1) {
        // Create a new array without the asset to be removed
        const updatedPlacedAssets = [...this.placedAssets];
        updatedPlacedAssets.splice(placedIndex, 1);
        this.placedAssets = updatedPlacedAssets;
        this.$store.commit('host/setPlacedAssets', updatedPlacedAssets);

        const updatedAssets = [...this.assets];
        asset.placed = false;
        asset.position = null;
        asset.inSpaces = [];
        updatedAssets.push(asset);
        this.assets = updatedAssets;
        this.$store.commit('host/setAssets', updatedAssets);
        
        // Show notification
        this.$emit('show-snackbar', `${this.getAssetDisplayName(asset)} removed from layout`);
      }
    },
    getSpacesForAsset(asset) {
      // Return all spaces that this asset is in
      if (!this.spaces || !asset) return [];
      
      // Ensure inSpaces is an array
      const inSpaces = asset.inSpaces || [];
      
      return this.spaces.filter(space => 
        inSpaces.includes(space.uuid)
      );
    },
    isIconInSpace(space) {
      return this.iconInSpaces && this.iconInSpaces.some(s => s.uuid === space.uuid);
    },
    updateSpaceName(space) {
      // The v-edit-dialog automatically updates the name in the item
      // We need to update the space in the SpaceEditor component
      if (this.$refs.spaceEditor) {
        this.$refs.spaceEditor.updateSpaceName(space.uuid, space.assetName);
      }
      
      // Create a copy of the placed assets array
      const updatedPlacedAssets = [...this.placedAssets];
      let hasUpdates = false;
      
      // Update the space name in placed assets
      updatedPlacedAssets.forEach((asset, idx) => {
        // Find the space in the asset's spaces array
        const spaceIndex = asset.spaces.findIndex(s => s.uuid === space.uuid);
        if (spaceIndex !== -1) {
          // Create a new spaces array with the updated space name
          const updatedSpaces = [...asset.spaces];
          updatedSpaces[spaceIndex] = {
            ...updatedSpaces[spaceIndex],
            name: space.assetName
          };
          
          // Update the asset with the new spaces array
          updatedPlacedAssets[idx] = {
            ...asset,
            spaces: updatedSpaces
          };
          
          hasUpdates = true;
        }
      });
      
      // Only update the state if there were changes
      if (hasUpdates) {
        this.placedAssets = updatedPlacedAssets;
      }
      
      // Show notification
      this.$emit('show-snackbar', `Space renamed to "${space.assetName}"`);
    },
    updateSpaceAllowOverlap(space) {
      // Show notification
      this.$emit('show-snackbar', `Space "${space.assetName}" overlap setting updated to ${space.allowOverlap ? 'enabled' : 'disabled'}`);
    },
    confirmDeleteSpace(space) {
      console.log('confirmDeleteSpace called with space:', space);
      this.checkDataIntegrity(); // Check data integrity before setting up the delete operation
      this.spaceToDelete = space;
      // Skip confirmation and delete directly
      this.deleteSpace();
    },
    checkDataIntegrity() {
      console.log('Checking data integrity:');
      console.log('spaces:', this.spaces, typeof this.spaces);
      console.log('removedSpaces:', this.removedSpaces, typeof this.removedSpaces);
      console.log('placedAssets:', this.placedAssets, typeof this.placedAssets);
      console.log('iconInSpace:', this.iconInSpace, typeof this.iconInSpace);
      console.log('iconInSpaces:', this.iconInSpaces, typeof this.iconInSpaces);
      
      // Check if any of these are undefined
      const issues = [];
      if (this.spaces === undefined) issues.push('spaces is undefined');
      if (this.removedSpaces === undefined) issues.push('removedSpaces is undefined');
      if (this.placedAssets === undefined) issues.push('placedAssets is undefined');
      if (this.iconInSpaces === undefined) issues.push('iconInSpaces is undefined');
      
      if (issues.length > 0) {
        console.warn('Data integrity issues found:', issues);
      } else {
        console.log('No data integrity issues found');
      }
    },
    deleteSpace() {
      if (!this.spaceToDelete) {
        return;
      }
      
      // Find the index of the space to delete
      const index = this.spaces.findIndex(s => s.uuid === this.spaceToDelete.uuid);
      
      if (index !== -1) {
        const spaceUuid = this.spaceToDelete.uuid;
        
        try {
          // Create a copy of the spaces array
          const updatedSpaces = [...this.spaces];
          
          // Check if lastUpdateOn exists and is not null
          if (updatedSpaces[index].lastUpdateOn) {
            // Add the space to removedSpaces using Vuex
            const updatedRemovedSpaces = [...this.removedSpaces, updatedSpaces[index]];
            this.$store.commit('host/setRemovedSpaces', updatedRemovedSpaces);
          }
        
          // Remove the space from the array
          updatedSpaces.splice(index, 1);
          this.spaces = updatedSpaces;
        
          // Update the SpaceEditor component
          if (this.$refs.spaceEditor) {
            this.$refs.spaceEditor.deleteSpace(spaceUuid);
          }
          
          // Ensure iconInSpaces is an array before filtering
          this.iconInSpaces = (this.iconInSpaces || []).filter(s => s.uuid !== spaceUuid);
        
          // Clear iconInSpace if it was in this space
          if (this.iconInSpace && this.iconInSpace.uuid === spaceUuid) {
            this.iconInSpace = this.iconInSpaces.length > 0 ? this.iconInSpaces[0] : null;
          }
          
          // Create a copy of the placed assets array
          const updatedPlacedAssets = [...(this.placedAssets || [])];
          
          let hasUpdates = false;
        
          // Update placed assets to remove this space from their inSpaces array
          updatedPlacedAssets.forEach((asset, idx) => {
            if (asset.inSpaces && asset.inSpaces.includes(spaceUuid)) {
              // Remove the space ID from inSpaces
              const updatedInSpaces = asset.inSpaces.filter(uuid => uuid !== spaceUuid);
              
              // Ensure spaces is an array before filtering
              const assetSpaces = asset.spaces || [];
              const updatedAssetSpaces = assetSpaces.filter(s => s.uuid !== spaceUuid);
              
              // Update the asset
              updatedPlacedAssets[idx] = {
                ...asset,
                inSpaces: updatedInSpaces,
                spaces: updatedAssetSpaces
              };
              
              hasUpdates = true;
            }
          });
          
          // Only update the state if there were changes
          if (hasUpdates) {
            this.placedAssets = updatedPlacedAssets;
          }
          
          // Force a redraw of the canvas after all updates are complete
          this.$nextTick(() => {
            if (this.$refs.spaceEditor) {
              this.$refs.spaceEditor.redrawCanvas();
            }
          });
        
          // Show notification
          this.$emit('show-snackbar', 'Space deleted successfully');
        } catch (error) {
          console.error('Error in deleteSpace method:', error);
          console.error('Error stack:', error.stack);
        }
      }
      
      // Close the dialog
      this.deleteDialog = false;
      this.spaceToDelete = null;
    },
    getImageName(imageUrl) {
      if (!imageUrl) return 'No image';
      
      // Extract filename from URL
      try {
        // For object URLs, just return a generic name
        if (imageUrl.startsWith('blob:')) {
          return 'Uploaded image';
        }
        
        // For regular URLs, try to extract the filename
        const url = new URL(imageUrl);
        const pathname = url.pathname;
        const filename = pathname.substring(pathname.lastIndexOf('/') + 1);
        
        return filename || 'Image';
      } catch (e) {
        return 'Image';
      }
    },
    getAssetInSpace(space) {
      // Return all assets that are in this space
      if (!this.placedAssets) return [];
      
      return this.placedAssets.filter(asset => 
        asset.inSpaces && asset.inSpaces.includes(space.uuid)
      );
    },
    handleResize() {
      // Debounce the resize event to avoid excessive updates
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      
      this.resizeTimeout = setTimeout(() => {
        this.updateContainerStyle();
      }, 200);
    },
    clearCanvas() {
      // Clear all data
      this.spaces = [];
      this.layoutImageUrl = null;
      this.backgroundFile = null;
      this.iconInSpace = null;
      this.iconInSpaces = [];
      this.placedAssets = [];
      
      // Reset asset spaces
      const updatedAssets = this.assets.map(item => ({
        ...item,
        inSpaces: [],
        placed: false,
        position: null
      }));
      this.assets = updatedAssets;
      
      // Show notification
      this.$emit('show-snackbar', 'Canvas cleared');
    },
    getAssetIcon(asset) {
      if (!asset) return 'mdi-camera';
      
      switch(asset.category ? asset.category.toLowerCase() : '') {
        case 'camera':
          return 'mdi-camera';
        case 'keypad':
          return 'mdi-dialpad';
        case 'lock':
          return 'mdi-lock';
        case 'keypad_lock':
          return 'mdi-lock-outline';
        case 'lock_button':
          return 'mdi-gesture-tap-button';
        default:
          return 'mdi-camera';
      }
    },
    getAssetColor(asset) {
      if (!asset) return 'grey';
      
      return this.getAssetTypeColor(asset.category);
    },
    getAssetTypeColor(category) {
      if (!category) return 'grey';
      
      const categoryLower = category.toLowerCase();
      
      switch(categoryLower) {
        case 'camera':
          return 'blue';
        case 'keypad':
          return 'green';
        case 'lock':
          return 'red';
        case 'keypad_lock':
          return 'purple';
        case 'lock_button':
          return 'orange';
        default:
          return 'grey';
      }
    },
    getAssetDisplayName(asset) {
      if (!asset) return '';
      
      // For cameras, show assetName-localIp format
      if (asset.category && asset.category.toLowerCase() === 'camera' && asset.localIp) {
        return `${asset.assetName}-${asset.localIp}`;
      }
      
      // For other asset types, just show the asset name
      return asset.assetName;
    },
    async saveLayoutData() {

      console.log('saveLayoutData', this.backgroundFile, this.layoutId)

      const imgKey = await this.putLayoutImage({imgFile: this.backgroundFile, layoutId: this.layoutId})

      // Ensure we have valid data for all properties
      const spaces = this.spaces || [];
      const removedSpaces = this.removedSpaces || [];
      const placedAssets = this.placedAssets || [];
      
      // Get available assets (assets that haven't been placed)
      const availableAssets = this.assets ? 
        this.assets.filter(item => !item.placed) : 
        [];

      // Prepare the data to be sent to the API
      const layoutData = {
        layoutId: this.layoutId,
        imgKey,
        dimensions: this.canvasDimensions,
        spaces,
        removedSpaces,
        placedAssets,
        availableAssets
      }
      
      console.log('Saving layout data:', layoutData);
      
      // Call the Vuex action with the prepared data
      this.saveLayout(layoutData)
        .then(() => {
          this.$emit('show-snackbar', 'Layout saved successfully');
          this.proceedWithLoadLayout();
        })
        .catch(error => {
          console.error('Error saving layout:', error);
          this.$emit('show-snackbar', 'Error saving layout: ' + (error.message || 'Unknown error'));
        })
    },
    async loadPropertySpaces() {
      console.log('loadPropertySpaces', this.property)
      
      await this.loadSpaces(this.property.hostPropertyCode)
    },
    ...mapActions({
      saveLayout: 'host/saveLayout',
      putLayoutImage: 'host/putLayoutImage',
      loadSpaces: 'host/loadSpaces',
      loadAssets: 'host/loadAssets'
    }),
    // Drag and drop methods
    handleDragStart(event, asset) {
      this.draggedAsset = asset;
      // Set data for drag operation
      event.dataTransfer.setData('text/plain', asset.uuid);
      // Set the drag image (optional)
      const icon = document.createElement('div');
      icon.innerHTML = `<i class="v-icon notranslate mdi mdi-${this.getAssetIcon(asset).replace('mdi-', '')}" style="color: ${this.getAssetColor(asset)}; font-size: 24px;"></i>`;
      document.body.appendChild(icon);
      event.dataTransfer.setDragImage(icon, 12, 12);
      setTimeout(() => {
        document.body.removeChild(icon);
      }, 0);
      
      // Select the asset
      this.selectAsset(asset);
    },
    
    handleDragOver(event) {
      // Prevent default to allow drop
      event.preventDefault();
    },
    
    handleDrop(event) {
      event.preventDefault();
      
      // Check if layout is loaded and if we have a current icon and dragged asset
      if (!this.layoutImageUrl || !this.currentIcon || !this.draggedAsset) {
        if (!this.layoutImageUrl) {
          this.$emit('show-snackbar', 'Please load a layout image before placing assets');
        }
        return;
      }
      
      // Get drop position relative to the canvas
      const canvasRect = this.$refs.spaceEditor.$el.getBoundingClientRect();
      const x = event.clientX - canvasRect.left;
      const y = event.clientY - canvasRect.top;
      
      // Update icon position in SpaceEditor
      if (this.$refs.spaceEditor) {
        this.$refs.spaceEditor.setIconPosition(x, y);
        
        // Check which spaces the icon is in at this position
        this.$refs.spaceEditor.checkIconInSpaces();
        
        // Place the asset
        this.placeAssetOnLayout();
      }
      
      // Reset dragged asset
      this.draggedAsset = null;
    },
    selectPlacedAsset(asset) {
      // Focus on the placed asset by updating the canvas view
      if (this.$refs.spaceEditor && asset.position) {
        // Set the icon position to the asset's position
        this.$refs.spaceEditor.setIconPosition(asset.position.x, asset.position.y);
        
        // Check which spaces the asset is in
        this.$refs.spaceEditor.checkIconInSpaces();
        
        // Show notification
        this.$emit('show-snackbar', `Focused on asset: ${this.getAssetDisplayName(asset)}`);
      }
    },
    // Drag and drop methods for assets from side menu
    handleAssetDragStart(asset) {
      // Set the current asset being dragged
      this.draggedAsset = asset;
      
      // Show notification
      this.$emit('show-snackbar', `Moving asset: ${this.getAssetDisplayName(asset)}`);
    },
    
    handleAssetPositionUpdated(data) {
      const { asset, spaces } = data;
      
      // Find the asset in the placed assets array
      const index = this.placedAssets.findIndex(item => item.uuid === asset.uuid);
      if (index !== -1) {
        // Get unique space UUIDs to prevent duplicates
        const uniqueSpaceUuids = [...new Set(spaces.map(space => space.uuid))];
        
        // Create a new array with the updated asset
        const updatedPlacedAssets = [...this.placedAssets];
        updatedPlacedAssets[index] = {
          ...updatedPlacedAssets[index],
          position: asset.position,
          spaces: spaces,
          inSpaces: uniqueSpaceUuids,
          layoutId: this.layoutId
        };
        this.placedAssets = updatedPlacedAssets;
        
        // Update the iconInSpaces if this is the current icon
        if (this.currentIcon && this.currentIcon.uuid === asset.uuid) {
          this.iconInSpaces = spaces;
          this.iconInSpace = spaces.length > 0 ? spaces[0] : null;
        }
      }
    },
    
    handleAssetDragEnd(asset) {
      // Reset dragged asset
      this.draggedAsset = null;
      
      // Find the asset in the placed assets array
      const index = this.placedAssets.findIndex(item => item.uuid === asset.uuid);
      if (index !== -1) {
        // Show notification about the new position
        const spacesText = this.placedAssets[index].spaces.length > 0 
          ? `now in ${this.placedAssets[index].spaces.length} space(s)` 
          : 'not in any space';
        
        this.$emit('show-snackbar', `Asset ${this.getAssetDisplayName(asset)} moved and is ${spacesText}`);
      }
    },
    handleAssetInNewSpace(data) {
      const { space, assets } = data;
      
      if (!assets || assets.length === 0) return;
      
      // Create a copy of the placed assets array
      const updatedPlacedAssets = [...this.placedAssets];
      let hasUpdates = false;
      
      // Update each asset that is in the new space
      assets.forEach(item => {
        // Find the asset in the placed assets array
        const index = updatedPlacedAssets.findIndex(eq => eq.uuid === item.uuid);
        if (index !== -1) {
          // Get all spaces this asset is in
          const spaces = [];
          this.spaces.forEach(s => {
            if (this.$refs.spaceEditor.isPointInPath(item.position, s.path)) {
              spaces.push(s);
            }
          });
          
          // Get unique space UUIDs to prevent duplicates
          const uniqueSpaceUuids = [...new Set(spaces.map(s => s.uuid))];
          
          // Update the asset's spaces
          updatedPlacedAssets[index] = {
            ...updatedPlacedAssets[index],
            spaces: spaces,
            inSpaces: uniqueSpaceUuids
          };
          hasUpdates = true;
        }
      });
      
      // Only update the state if there were changes
      if (hasUpdates) {
        this.placedAssets = updatedPlacedAssets;
      }
      
      // Show notification
      const assetNames = assets.map(item => this.getAssetDisplayName(item)).join(', ');
      this.$emit('show-snackbar', `${assets.length} assets (${assetNames}) automatically associated with new space: ${space.assetName}`);
    },
    confirmLoadLayoutFromS3() {
      this.loadLayoutDialog = true;
    },
    
    proceedWithLoadLayout() {
      this.loadLayoutDialog = false;
      
      // Clear all WIP states before loading
      this.spaces = [];
      this.iconInSpace = null;
      this.iconInSpaces = [];
      this.placedAssets = [];
      this.$store.commit('host/setRemovedSpaces', []); // Clear removedSpaces
      
      // Reset asset data
      if (this.assets) {
        const updatedAssets = this.assets.map(item => ({
          ...item,
          inSpaces: [],
          placed: false,
          position: null
        }));
        this.assets = updatedAssets;
      }
      
      // Reset current icon
      this.currentIcon = null;
      
      this.loadLayoutFromS3();
    },
    async loadLayoutFromS3() {
      try {
        // Check if property and layouts exist
        if (!this.$store.state.host.property || !this.$store.state.host.property.layouts) {
          console.log('No property or layouts found');
          this.$emit('show-snackbar', 'No property or layouts found');
          return;
        }
        
        const layouts = this.$store.state.host.property.layouts;
        if (layouts.length === 0) {
          console.log('No layouts available');
          this.$emit('show-snackbar', 'No layouts available');
          return;
        }
        
        // Get the latest layout (assuming the last one in the array is the most recent)
        const layoutIndex = layouts.length - 1;
        this.layoutId = layoutIndex;
        const layoutKey = layouts[layoutIndex];
        
        if (!layoutKey) {
          console.log('Layout key is empty');
          this.$emit('show-snackbar', 'Layout key is empty');
          return;
        }
        
        console.log('Loading layout from S3:', layoutKey);
        
        // Show loading notification
        this.$emit('show-snackbar', 'Loading layout from S3...');
        
        // Clear any unsaved spaces in the SpaceEditor component
        if (this.$refs.spaceEditor) {
          // Reset internal spaces in the SpaceEditor
          this.$refs.spaceEditor.internalSpaces = [];
          this.$refs.spaceEditor.iconInSpace = null;
          this.$refs.spaceEditor.iconInSpaces = [];
        }
        
        // Clear spaces in the parent component
        this.spaces = [];
        this.iconInSpace = null;
        this.iconInSpaces = [];
        this.placedAssets = [];
        
        // Reset asset data
        if (this.assets) {
          this.assets.forEach(item => {
            item.inSpaces = [];
            item.placed = false;
            item.position = null;
          });
        }
        
        // Reset current icon
        this.currentIcon = null;
        
        // Directly get the S3 image URL using Amplify Storage
        try {
          const imageUrl = await Storage.get(layoutKey, { level: 'private', download: true });
          console.log('Got S3 image URL or blob:', imageUrl);
          
          // If we got a blob, create an object URL
          let displayUrl;
          if (imageUrl.Body) {
            // We got a blob, create an object URL
            const blob = imageUrl.Body;
            displayUrl = URL.createObjectURL(blob);
            console.log('Created object URL from blob:', displayUrl);
            
            // Save the blob as backgroundFile directly
            this.backgroundFile = new File([blob], layoutKey.split('/').pop(), { type: blob.type || 'image/jpeg' });
          } else {
            // We got a URL directly
            displayUrl = imageUrl;
          }
          
          // Test if the URL is accessible (CORS check)
          try {
            const testRequest = new XMLHttpRequest();
            testRequest.open('HEAD', displayUrl, false);
            testRequest.send();
            console.log('CORS check status:', testRequest.status);
            if (testRequest.status >= 200 && testRequest.status < 300) {
              console.log('CORS check passed');
            } else {
              console.warn('CORS check failed with status:', testRequest.status);
            }
          } catch (corsError) {
            console.warn('CORS check error:', corsError);
          }
          
          // Set as regular layout image URL (same as local files)
          this.layoutImageUrl = displayUrl;
          console.log('Set layoutImageUrl to:', this.layoutImageUrl);
          this.backgroundFile = null;
          
          // Create a temporary Image object to get the actual dimensions
          const img = new Image();
          img.onload = async () => {
            // Update dimensions based on the loaded image
            const originalWidth = img.width;
            const originalHeight = img.height;
            
            // If height exceeds 1000px, maintain aspect ratio
            if (originalHeight > 1000) {
              const aspectRatio = originalWidth / originalHeight;
              const newHeight = 1000;
              const newWidth = Math.round(newHeight * aspectRatio);
              
              this.canvasDimensions = { 
                width: newWidth, 
                height: newHeight 
              };
            } else {
              this.canvasDimensions = { 
                width: originalWidth, 
                height: originalHeight 
              };
            }
            
            // Fetch the image as a blob to use as backgroundFile
            try {
              const response = await fetch(displayUrl);
              const blob = await response.blob();
              this.backgroundFile = new File([blob], layoutKey.split('/').pop(), { type: blob.type });
            } catch (error) {
              console.error('Error fetching image as blob:', error);
              // Continue without setting backgroundFile
            }
            
            // Update container style
            this.updateContainerStyle();
            
            // Update the SpaceEditor component
            if (this.$refs.spaceEditor) {
              this.$refs.spaceEditor.updateCanvasDimensions();
            }
            
            console.log('S3 image dimensions:', this.canvasDimensions);
            
            // Load spaces and assets for this layout
            console.log('Starting to load spaces and assets for layout');
            
            this.loadPropertySpaces().then(() => {
              console.log('Spaces loaded successfully:', this.spaces);
              return this.loadAssets();
            }).then(() => {
              console.log('Assets loaded successfully:', this.assets);
              console.log('Placed assets:', this.placedAssets);
              
              // Success notification
              this.$emit('show-snackbar', 'Layout loaded successfully from S3');
              
              // Update the SpaceEditor component with the loaded spaces and assets
              console.log('About to redraw canvas with loaded spaces and assets');
              if (this.$refs.spaceEditor) {
                this.$refs.spaceEditor.redrawCanvas();
                console.log('Canvas redrawn');
              }
            }).catch(error => {
              console.error('Error loading spaces or assets:', error);
              this.$emit('show-snackbar', 'Layout loaded, but there was an error loading spaces or assets');
            });
          };
          
          img.onerror = (err) => {
            console.error('Error loading S3 image:', err);
            this.$emit('show-snackbar', 'Error loading S3 image: ' + (err.message || 'Unknown error'));
          };
          
          // Start loading the image
          img.src = displayUrl;
        } catch (error) {
          console.error('Error getting S3 image URL:', error);
          this.$emit('show-snackbar', 'Error getting S3 image URL: ' + (error.message || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error loading layout from S3:', error);
        this.$emit('show-snackbar', 'Error loading layout: ' + (error.message || 'Unknown error'));
      }
    },
  }
}
</script>

<style scoped>
.geometry-data {
  background-color: #f5f5f5;
  padding: 10px;
  border-radius: 4px;
  overflow-x: auto;
  max-height: 300px;
  font-family: monospace;
}

ol, ul {
  padding-left: 20px;
}

li {
  margin-bottom: 8px;
}

.canvas-container {
  max-width: 100%;
  height: auto;
  display: flex;
  justify-content: center;
}

.position-relative {
  position: relative;
}

/* Side menu styles for assets */
.asset-side-menu {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  z-index: 10;
  display: flex;
  transition: transform 0.3s ease;
  transform: translateX(calc(100% - 40px));
}

.asset-side-menu-expanded {
  transform: translateX(0);
}

.asset-side-menu-tab {
  width: 40px;
  background-color: #1976D2;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1);
}

.asset-side-menu-content {
  width: 300px;
  background-color: white;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  border-left: 1px solid #e0e0e0;
}

/* Side menu styles for spaces */
.spaces-side-menu {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  z-index: 10;
  display: flex;
  transition: transform 0.3s ease;
  transform: translateX(calc(-100% + 40px));
}

.spaces-side-menu-expanded {
  transform: translateX(0);
}

.spaces-side-menu-tab {
  width: 40px;
  background-color: #4CAF50;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
  order: 2;
}

.spaces-side-menu-content {
  width: 500px;
  background-color: white;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e0e0e0;
  order: 1;
}

.asset-list, .spaces-list {
  flex: 1;
  overflow-y: auto;
  max-height: 500px;
}

.asset-item-selected {
  background-color: rgba(25, 118, 210, 0.1);
}

.placed-asset-item {
  cursor: pointer;
  background-color: rgba(0, 0, 0, 0.02);
}

.placed-asset-item:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.space-list-item {
  cursor: pointer;
  background-color: rgba(0, 0, 0, 0.02);
}

.space-list-item:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

/* Custom styling for spaces list items */
.spaces-list .v-list-item {
  font-size: 1.1rem;
  padding: 12px 16px;
}

.spaces-list .v-list-item__icon {
  margin-right: 16px;
}

.spaces-list .v-icon {
  font-size: 1.5rem;
}

.space-name {
  font-size: 1.2rem;
  font-weight: 500;
  margin-bottom: 8px;
}

.space-name-display {
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px dashed rgba(0, 0, 0, 0.2);
  display: inline-block;
  transition: all 0.2s ease;
}

.space-name-display:hover {
  background-color: rgba(76, 175, 80, 0.1);
  border-color: rgba(76, 175, 80, 0.4);
  cursor: pointer;
}

.space-name-edit .v-input__slot {
  border: 2px solid rgba(76, 175, 80, 0.6) !important;
  background-color: white !important;
}

.spaces-list .v-list-item-subtitle {
  font-size: 0.95rem;
}
</style> 